import { useState, useMemo, useEffect } from 'react';
import { useRWA } from './useRWA';
import type { RWAAsset, Listing, AssetCategory, AssetType, PaymentToken } from './rwa-service';
import { Store, Briefcase, ShieldCheck, Settings, RefreshCw, Coins, X, DollarSign, Tag, Lock } from 'lucide-react';
import './App.css';

// Get packageId from URL params or fetch from API
function getPackageIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('packageId');
}

interface PackageConfig {
  packageId: string | null;
  operatorParty: string | null;
  publicParty: string | null;
}

async function fetchPackageConfig(): Promise<PackageConfig> {
  try {
    const response = await fetch('/api/package');
    const data = await response.json();
    console.log('[RWA] Package config fetched:', data);
    return {
      packageId: data.packageId || null,
      operatorParty: data.operatorParty || null,
      publicParty: data.publicParty || null,
    };
  } catch (err) {
    console.error('[RWA] Failed to fetch package config:', err);
    return { packageId: null, operatorParty: null, publicParty: null };
  }
}

async function saveOperatorParty(operatorParty: string): Promise<void> {
  try {
    await fetch('/api/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operatorParty }),
    });
  } catch (err) {
    console.error('Failed to save operator party:', err);
  }
}

const ASSET_CATEGORIES: AssetCategory[] = [
  'RealEstate',
  'Commodities',
  'Art',
  'Collectibles',
  'Securities',
  'Infrastructure',
  'IntellectualProperty',
];

type TabId = 'marketplace' | 'portfolio' | 'operator' | 'issuer' | 'verifier';

const ipfsToHttp = (url: string) =>
  url?.startsWith('ipfs://') ? `https://gateway.pinata.cloud/ipfs/${url.slice(7)}` : url;

function getMediaType(url: string, hint?: string): 'model' | 'image' | 'video' | 'pdf' | 'unknown' {
  const lower = url.toLowerCase();
  if (lower.endsWith('.glb') || lower.endsWith('.gltf')) return 'model';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.webp') || lower.endsWith('.gif')) return 'image';
  if (lower.endsWith('.mp4') || lower.endsWith('.webm')) return 'video';
  if (lower.endsWith('.pdf')) return 'pdf';
  // Infer from metadata key hint (IPFS gateway URLs have no extension)
  if (hint) {
    const h = hint.toLowerCase();
    if (h.includes('model') || h.includes('glb') || h.includes('gltf')) return 'model';
    if (h.includes('preview') || h.includes('image') || h.includes('photo')) return 'image';
    if (h.includes('video')) return 'video';
  }
  return 'unknown';
}

function App() {
  const [packageId, setPackageId] = useState<string | null>(null);
  const [configOperatorParty, setConfigOperatorParty] = useState<string | null>(null);
  const [configPublicParty, setConfigPublicParty] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Load model-viewer web component for 3D NFTs
  useEffect(() => {
    if (!document.querySelector('script[src*="model-viewer"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const urlPackageId = getPackageIdFromUrl();
    if (urlPackageId) {
      setPackageId(urlPackageId);
      fetchPackageConfig().then(config => {
        setConfigOperatorParty(config.operatorParty);
        setConfigPublicParty(config.publicParty);
        setConfigLoading(false);
      });
    } else {
      fetchPackageConfig().then(config => {
        if (config.packageId) setPackageId(config.packageId);
        setConfigOperatorParty(config.operatorParty);
        setConfigPublicParty(config.publicParty);
        setConfigLoading(false);
      });
    }
  }, []);

  const rwaState = useRWA({
    packageId: packageId || '',
    operatorParty: configOperatorParty || undefined,
    publicParty: configPublicParty || undefined
  });

  const {
    connected,
    partyId,
    loading,
    dataLoaded,
    error,
    roles,
    assets,
    myHoldings,
    myPaymentBalance,
    listings,
    myProfile,
    issuerLicenses,
    assetProposals,
    listingProposals,
    depositRequests,
    pendingTrades,
    allInvestorProfiles,
    myIssuedAssets,
    platformInitialized,
    initializePlatform,
    selfRegisterAsInvestor,
    createListingProposal,
    myPaymentHoldings,
    purchaseFromListing,
    delistAsset,
    withdrawListingProposal,
    requestPaymentDeposit,
    appointIssuer,
    revokeIssuer,
    approveAssetProposal,
    rejectAssetProposal,
    approveListingProposal,
    rejectListingProposal,
    approvePaymentDeposit,
    autoSettleTrades,
    unlockTokens,
    archiveStuckTrade: _archiveStuckTrade,
    proposeAsset,
    mintTokens,
    verifyInvestor,
    refresh,
  } = rwaState;

  const [activeTab, setActiveTab] = useState<TabId>('marketplace');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState<RWAAsset | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [newIssuer, setNewIssuer] = useState({ party: '', name: '', categories: [] as AssetCategory[] });
  const [newAsset, setNewAsset] = useState({
    assetId: '',
    name: '',
    symbol: '',
    category: 'RealEstate' as AssetCategory,
    assetType: 'Fungible' as AssetType,
    totalSupply: 1000,
    description: '',
    valuationAmount: '0',
  });
  const [mintForm, setMintForm] = useState({ assetContractId: '', recipient: '', amount: 0 });
  const [verifyForm, setVerifyForm] = useState({
    investorParty: '',
    status: 'KYCVerified' as 'Unverified' | 'KYCVerified' | 'AccreditedVerified' | 'InstitutionalVerified',
    jurisdiction: 'US',
  });

  // Listing modal
  const [listingModal, setListingModal] = useState<{ asset: RWAAsset; holdingCid: string } | null>(null);
  const [listingForm, setListingForm] = useState({ amount: '', pricePerToken: '' });

  // Purchase modal
  const [purchaseModal, setPurchaseModal] = useState<Listing | null>(null);
  const [purchaseForm, setPurchaseForm] = useState({ amount: '' });

  // Deposit modal
  const [depositModal, setDepositModal] = useState(false);
  const [depositForm, setDepositForm] = useState({ amount: '' });

  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{ success: boolean; message: string } | null>(null);

  // Platform init form
  const [platformForm, setPlatformForm] = useState({
    platformName: 'RWA Platform',
    platformFeePercent: '0.25',
  });

  // Registration form
  const [registrationForm, setRegistrationForm] = useState({
    jurisdiction: 'US',
  });

  const handleInitializePlatform = async () => {
    if (!partyId || !configPublicParty) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      await initializePlatform({
        platformName: platformForm.platformName,
        platformFeePercent: platformForm.platformFeePercent,
        feeCollector: partyId,
        operator: partyId,
        publicParty: configPublicParty,
      });
      // Save operator party to KV for other users to discover
      await saveOperatorParty(partyId);
      setConfigOperatorParty(partyId);
      setActionResult({ success: true, message: 'Platform initialized successfully!' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed to initialize' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelfRegister = async () => {
    if (!configOperatorParty) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      await selfRegisterAsInvestor(configOperatorParty, registrationForm.jurisdiction);
      setActionResult({ success: true, message: 'Registration complete! You are now registered as an investor.' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed to register' });
    } finally {
      setActionLoading(false);
    }
  };

  // Build available tabs based on roles
  const tabIcons: Record<TabId, React.ReactNode> = {
    marketplace: <Store size={15} />,
    portfolio: <Briefcase size={15} />,
    operator: <Settings size={15} />,
    issuer: <Coins size={15} />,
    verifier: <ShieldCheck size={15} />,
  };

  const availableTabs = useMemo(() => {
    const tabs: { id: TabId; label: string; badge?: number }[] = [
      { id: 'marketplace', label: 'Marketplace' },
      { id: 'portfolio', label: 'Portfolio', badge: myHoldings.length || undefined },
    ];

    if (roles?.isOperator) {
      tabs.push({
        id: 'operator',
        label: 'Operator',
        badge: assetProposals.length + listingProposals.length + depositRequests.length + pendingTrades.length || undefined,
      });
    }

    if (roles?.isIssuer) {
      tabs.push({
        id: 'issuer',
        label: 'Issuer',
        badge: myIssuedAssets.length || undefined,
      });
    }

    if (roles?.isVerifier) {
      tabs.push({ id: 'verifier', label: 'Verifier' });
    }

    return tabs;
  }, [roles, myHoldings.length, assetProposals.length, listingProposals.length, depositRequests.length, pendingTrades.length, myIssuedAssets.length]);

  // Filter marketplace listings (by category via cross-reference with assets)
  const filteredListings = useMemo(() => {
    return listings.filter(listing => {
      const matchesSearch =
        listing.assetName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        listing.description.toLowerCase().includes(searchQuery.toLowerCase());
      if (selectedCategory === 'all') return matchesSearch;
      // Cross-reference with assets for category filtering
      const asset = assets.find(a => a.assetId === listing.assetId);
      return matchesSearch && asset?.category === selectedCategory;
    });
  }, [listings, assets, selectedCategory, searchQuery]);

  // Get listings for selected asset
  const assetListings = useMemo(() => {
    if (!selectedAsset) return [];
    return listings.filter(l => l.assetId === selectedAsset.assetId);
  }, [listings, selectedAsset]);

  const formatPrice = (price: number): string => {
    return price.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const truncateParty = (id: string): string => {
    if (id.length <= 20) return id;
    return `${id.substring(0, 10)}...${id.substring(id.length - 6)}`;
  };

  const handleCreateListingProposal = async () => {
    if (!listingModal) return;
    const amount = parseFloat(listingForm.amount);
    const price = parseFloat(listingForm.pricePerToken);
    if (!amount || !price) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      const defaultPaymentToken: PaymentToken = { symbol: 'USDC', issuer: configOperatorParty || '', decimals: '6' };
      await createListingProposal(
        listingModal.holdingCid,
        {
          assetId: listingModal.asset.assetId,
          assetSymbol: listingModal.asset.symbol,
          assetName: listingModal.asset.name,
          description: listingModal.asset.description || listingModal.asset.underlying.description,
          imageUrl: listingModal.asset.imageUrl,
        },
        amount,
        price,
        defaultPaymentToken
      );
      setActionResult({ success: true, message: 'Listing proposal submitted! Waiting for operator approval.' });
      setListingModal(null);
      setListingForm({ amount: '', pricePerToken: '' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed to create listing' });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!purchaseModal) return;
    const amount = parseFloat(purchaseForm.amount) || purchaseModal.amount;
    setActionLoading(true);
    setActionResult(null);
    try {
      if (purchaseModal.seller === partyId) throw new Error('Cannot purchase your own listing.');
      const tokenSymbol = purchaseModal.paymentToken || 'USDC';
      const paymentHolding = myPaymentHoldings.find(b => b.token === tokenSymbol && (b.amount - b.lockedAmount) >= amount);
      if (!paymentHolding) throw new Error(`No payment holding with sufficient unlocked balance (need ${amount} ${tokenSymbol}). Please deposit more funds.`);

      await purchaseFromListing(purchaseModal.contractId, paymentHolding.contractId, amount);
      setActionResult({ success: true, message: 'Purchase complete!' });
      setPurchaseModal(null);
      setPurchaseForm({ amount: '' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDeposit = async () => {
    const amount = parseFloat(depositForm.amount);
    if (!amount) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      const defaultPaymentToken: PaymentToken = { symbol: 'USDC', issuer: configOperatorParty || '', decimals: '6' };
      await requestPaymentDeposit(defaultPaymentToken, amount);
      setActionResult({ success: true, message: 'Deposit request submitted! Waiting for operator approval.' });
      setDepositModal(false);
      setDepositForm({ amount: '' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed to request deposit' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveListingProposal = async (proposalId: string) => {
    setActionLoading(true);
    try {
      await approveListingProposal(proposalId);
      setActionResult({ success: true, message: 'Listing approved' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectListingProposal = async (proposalId: string) => {
    setActionLoading(true);
    try {
      await rejectListingProposal(proposalId, 'Rejected by operator');
      setActionResult({ success: true, message: 'Listing rejected' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveDeposit = async (requestId: string) => {
    setActionLoading(true);
    try {
      await approvePaymentDeposit(requestId);
      setActionResult({ success: true, message: 'Deposit approved' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAppointIssuer = async () => {
    if (!newIssuer.party || !newIssuer.name || newIssuer.categories.length === 0) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      await appointIssuer(newIssuer.party, newIssuer.name, newIssuer.categories);
      setActionResult({ success: true, message: 'Issuer proposal created' });
      setNewIssuer({ party: '', name: '', categories: [] });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveAsset = async (proposalId: string) => {
    setActionLoading(true);
    try {
      await approveAssetProposal(proposalId);
      setActionResult({ success: true, message: 'Asset approved' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectAsset = async (proposalId: string) => {
    setActionLoading(true);
    try {
      await rejectAssetProposal(proposalId, 'Rejected by operator');
      setActionResult({ success: true, message: 'Asset rejected' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSettleTrade = async (_tradeId: string) => {
    setActionLoading(true);
    try {
      const count = await autoSettleTrades();
      setActionResult({ success: true, message: `Settled ${count} trade(s)` });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed to settle' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleProposeAsset = async () => {
    if (!newAsset.assetId || !newAsset.name) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      await proposeAsset({
        assetId: newAsset.assetId,
        name: newAsset.name,
        symbol: newAsset.symbol || newAsset.assetId,
        category: newAsset.category,
        assetType: newAsset.assetType,
        totalSupply: newAsset.totalSupply,
        compliance: { requireKYC: true },
        underlying: {
          description: newAsset.description,
          valuationAmount: newAsset.valuationAmount,
          valuationCurrency: 'USD',
        },
      });
      setActionResult({ success: true, message: 'Asset proposal submitted' });
      setNewAsset({
        assetId: '',
        name: '',
        symbol: '',
        category: 'RealEstate',
        assetType: 'Fungible',
        totalSupply: 1000,
        description: '',
        valuationAmount: '0',
      });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleMint = async () => {
    if (!mintForm.assetContractId || !mintForm.recipient || mintForm.amount <= 0) return;
    setActionLoading(true);
    try {
      await mintTokens(mintForm.assetContractId, mintForm.recipient, mintForm.amount);
      setActionResult({ success: true, message: `Minted ${mintForm.amount} tokens` });
      setMintForm({ assetContractId: '', recipient: '', amount: 0 });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyForm.investorParty) return;
    setActionLoading(true);
    try {
      await verifyInvestor(verifyForm.investorParty, verifyForm.status, verifyForm.jurisdiction);
      setActionResult({ success: true, message: 'Investor verified' });
      setVerifyForm({ investorParty: '', status: 'KYCVerified', jurisdiction: 'US' });
    } catch (err) {
      setActionResult({ success: false, message: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setActionLoading(false);
    }
  };

  if (configLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading configuration...</p>
      </div>
    );
  }

  if (!packageId) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <h2>Configuration Error</h2>
        <p>Could not load package ID. Please check that the DAR is deployed and the API is configured.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <h2>Connection Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!connected || !partyId) {
    return (
      <div className="not-connected">
        <h2>Not Connected</h2>
        <p>Please log in to the portal.</p>
      </div>
    );
  }

  // Determine if platform needs initialization
  // If configOperatorParty is set, platform was already initialized by someone
  const platformNeedsInit = !platformInitialized && !configOperatorParty;

  // Show init screen only if platform truly doesn't exist
  if (platformNeedsInit) {
    return (
      <div className="app">
        <div className="init-container">
          <h1>Initialize RWA Platform</h1>
          <p>The platform has not been initialized yet. As the first user, you will become the operator.</p>

          {actionResult && (
            <div className={`action-result ${actionResult.success ? 'success' : 'error'}`}>
              {actionResult.message}
            </div>
          )}

          <div className="init-form">
            <div className="form-group">
              <label>Platform Name</label>
              <input
                type="text"
                value={platformForm.platformName}
                onChange={e => setPlatformForm({ ...platformForm, platformName: e.target.value })}
                placeholder="RWA Platform"
              />
            </div>
            <div className="form-group">
              <label>Platform Fee (%)</label>
              <input
                type="text"
                value={platformForm.platformFeePercent}
                onChange={e => setPlatformForm({ ...platformForm, platformFeePercent: e.target.value })}
                placeholder="0.25"
              />
            </div>
            <div className="form-group">
              <label>Operator (You)</label>
              <input type="text" value={partyId} disabled />
            </div>
            <button
              onClick={handleInitializePlatform}
              disabled={actionLoading || !platformForm.platformName}
              className="init-btn"
            >
              {actionLoading ? 'Initializing...' : 'Initialize Platform'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show registration prompt if platform exists but user not registered
  // (either we can see platform, or configOperatorParty indicates it exists)
  if (!myProfile) {
    return (
      <div className="app">
        <div className="init-container">
          <h1>Register as Investor</h1>
          <p>Welcome! Register to join the RWA marketplace.</p>

          {actionResult && (
            <div className={`action-result ${actionResult.success ? 'success' : 'error'}`}>
              {actionResult.message}
            </div>
          )}

          <div className="init-form">
            <div className="form-group">
              <label>Your Party ID</label>
              <input type="text" value={partyId} disabled />
            </div>
            <div className="form-group">
              <label>Jurisdiction</label>
              <input
                type="text"
                value={registrationForm.jurisdiction}
                onChange={e => setRegistrationForm({ ...registrationForm, jurisdiction: e.target.value })}
                placeholder="US"
              />
              <small className="form-hint">
                Your country/jurisdiction code (e.g., US, EU, UK)
              </small>
            </div>
            <button
              onClick={handleSelfRegister}
              disabled={actionLoading || !registrationForm.jurisdiction || !configOperatorParty}
              className="init-btn"
            >
              {actionLoading ? 'Registering...' : 'Register as Investor'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <h1>RWA Platform</h1>
          <div className="party-id-badge" title={partyId}>
            <span className="party-value">{truncateParty(partyId)}</span>
          </div>
          {roles && (
            <div className="role-badges">
              {roles.isOperator && <span className="role-badge operator">Operator</span>}
              {roles.isIssuer && <span className="role-badge issuer">Issuer</span>}
              {roles.isVerifier && <span className="role-badge verifier">Verifier</span>}
            </div>
          )}
        </div>
        <div className="header-right">
          <div className="balance-badge">
            <span className="balance-value">{formatPrice(myPaymentBalance)}</span>
          </div>
          <button className="refresh-btn" onClick={refresh}><RefreshCw size={14} /> Refresh</button>
        </div>
      </header>

      {/* Result Banner */}
      {actionResult && (
        <div className={`action-result ${actionResult.success ? 'success' : 'error'}`}>
          {actionResult.message}
          <button onClick={() => setActionResult(null)}>x</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {availableTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tabIcons[tab.id]}
            {tab.label}
            {tab.badge && <span className="tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* ============================================ */}
      {/* MARKETPLACE TAB - Shows listings, not assets */}
      {/* ============================================ */}
      {activeTab === 'marketplace' && (
        <div className="tab-content">
          <div className="filters">
            <input
              type="text"
              placeholder="Search listings..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
              <option value="all">All Categories</option>
              {ASSET_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="asset-grid">
            {filteredListings.map(listing => {
              const asset = assets.find(a => a.assetId === listing.assetId);
              const modelCID = asset?.metadata?.find(m => m.key === 'modelCID')?.value;
              return (
                <div key={listing.contractId} className="asset-card" onClick={() => setPurchaseModal(listing)}>
                  {(() => {
                    if (modelCID) {
                      const modelUrl = ipfsToHttp(modelCID.startsWith('ipfs://') ? modelCID : `ipfs://${modelCID}`);
                      return (
                        <div className="asset-thumbnail">
                          {/* @ts-expect-error model-viewer is a web component */}
                          <model-viewer src={modelUrl} alt={listing.assetName} auto-rotate camera-controls loading="lazy" style={{ width: '100%', height: '100%', background: 'transparent' }} />
                        </div>
                      );
                    }
                    if (listing.imageUrl) {
                      return (
                        <div className="asset-thumbnail">
                          <img src={ipfsToHttp(listing.imageUrl)} alt={listing.assetName} />
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="asset-header">
                    {asset && <span className="asset-category">{asset.category}</span>}
                    <span className="asset-type">{listing.paymentToken}</span>
                  </div>
                  <h3>{listing.assetName}</h3>
                  <p>{listing.description}</p>
                  <div className="asset-stats">
                    <div>
                      <span className="label">Price</span>
                      <span className="value">{formatPrice(listing.pricePerToken)}</span>
                    </div>
                    <div>
                      <span className="label">Amount</span>
                      <span className="value">{listing.amount}</span>
                    </div>
                    <div>
                      <span className="label">Total</span>
                      <span className="value">{formatPrice(listing.totalPrice)}</span>
                    </div>
                  </div>
                  {(() => {
                    const principal = asset?.metadata?.find(m => m.key === 'principalAmount')?.value;
                    if (!principal || principal === '0') return null;
                    const rate = asset?.metadata?.find(m => m.key === 'interestRate')?.value;
                    return (
                      <div className="asset-stats" style={{ marginTop: '0.25rem' }}>
                        <div><span className="label">Principal</span><span className="value">{formatPrice(parseFloat(principal))}</span></div>
                        {rate && rate !== '0' && <div><span className="label">Rate</span><span className="value">{rate}%</span></div>}
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', opacity: 0.6 }}>
                    Seller: {truncateParty(listing.seller)}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredListings.length === 0 && !dataLoaded && (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Loading listings...</p>
            </div>
          )}
          {filteredListings.length === 0 && dataLoaded && (
            <div className="empty-state">No listings available. Assets must be listed for sale by their owners.</div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* PORTFOLIO TAB */}
      {/* ============================================ */}
      {activeTab === 'portfolio' && (
        <div className="tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>My Holdings</h2>
            <button onClick={() => setDepositModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <DollarSign size={14} /> Deposit Funds
            </button>
          </div>
          {myHoldings.length === 0 ? (
            <div className="empty-state">No holdings yet</div>
          ) : (
            <div className="asset-grid">
              {myHoldings.map(holding => {
                const asset = assets.find(a => a.assetId === holding.assetId);
                if (!asset) return null;
                // Match by holding contract ID
                const activeListing = listings.find(l => l.sellerHoldingCid === holding.contractId);
                const hasActiveListing = !!activeListing;
                const pendingProposal = listingProposals.find(p => p.sellerHoldingCid === holding.contractId);
                const hasPendingProposal = !!pendingProposal;
                return (
                  <div key={holding.contractId} className="asset-card owned">
                    {(() => {
                      const modelCID = asset.metadata?.find(m => m.key === 'modelCID')?.value;
                      if (modelCID) {
                        const modelUrl = ipfsToHttp(modelCID.startsWith('ipfs://') ? modelCID : `ipfs://${modelCID}`);
                        return (
                          <div className="asset-thumbnail" onClick={() => setSelectedAsset(asset)}>
                            {/* @ts-expect-error model-viewer is a web component */}
                            <model-viewer src={modelUrl} alt={asset.name} auto-rotate camera-controls loading="lazy" style={{ width: '100%', height: '100%', background: 'transparent' }} />
                          </div>
                        );
                      }
                      if (asset.imageUrl) {
                        return (
                          <div className="asset-thumbnail" onClick={() => setSelectedAsset(asset)}>
                            <img src={ipfsToHttp(asset.imageUrl)} alt={asset.name} />
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <span className="owned-badge">Owned</span>
                    {hasActiveListing && <span className="owned-badge" style={{ left: 'auto', right: '0.5rem', background: '#22c55e' }}>Listed</span>}
                    {hasPendingProposal && !hasActiveListing && <span className="owned-badge" style={{ left: 'auto', right: '0.5rem', background: '#eab308' }}>Pending Listing</span>}
                    <h3 onClick={() => setSelectedAsset(asset)} style={{ cursor: 'pointer' }}>{asset.name}</h3>
                    <div className="asset-stats">
                      <div>
                        <span className="label">Available</span>
                        <span className="value">{holding.amount || 0}</span>
                      </div>
                      {(holding.lockedAmount || 0) > 0 && (
                        <div>
                          <span className="label">Locked</span>
                          <span className="value">{holding.lockedAmount}</span>
                        </div>
                      )}
                      <div>
                        <span className="label">Valuation</span>
                        <span className="value">{formatPrice(parseFloat(asset.underlying.valuationAmount || '0'))}</span>
                      </div>
                    </div>
                    {(() => {
                      const principal = asset.metadata?.find(m => m.key === 'principalAmount')?.value;
                      if (!principal || principal === '0') return null;
                      const rate = asset.metadata?.find(m => m.key === 'interestRate')?.value;
                      return (
                        <div className="asset-stats" style={{ marginTop: '0.25rem' }}>
                          <div><span className="label">Principal</span><span className="value">{formatPrice(parseFloat(principal))}</span></div>
                          {rate && rate !== '0' && <div><span className="label">Rate</span><span className="value">{rate}%</span></div>}
                        </div>
                      );
                    })()}
                    {/* Debug: CID matching info */}
                    <div style={{ fontSize: '0.6rem', opacity: 0.4, marginTop: '0.25rem', wordBreak: 'break-all' }}>
                      H:{holding.contractId.substring(0,12)} | L:{activeListing?.sellerHoldingCid?.substring(0,12) || 'none'} | P:{pendingProposal?.sellerHoldingCid?.substring(0,12) || 'none'}
                    </div>
                    {hasActiveListing ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeListing) {
                              delistAsset(activeListing.contractId, holding.contractId, activeListing.amount)
                                .then(() => refresh())
                                .catch(err => alert('Delist failed: ' + (err?.message || err)));
                            }
                          }}
                          className="btn-danger"
                          style={{ marginTop: '0.5rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', background: '#dc2626' }}
                        >
                          <Tag size={14} /> Delist
                        </button>
                    ) : hasPendingProposal ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (pendingProposal) {
                              withdrawListingProposal(pendingProposal.contractId, holding.contractId, pendingProposal.amount)
                                .then(() => refresh())
                                .catch(err => alert('Withdraw failed: ' + (err?.message || err)));
                            }
                          }}
                          style={{ marginTop: '0.5rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', background: '#eab308', color: '#000' }}
                        >
                          <Tag size={14} /> Withdraw Proposal
                        </button>
                    ) : holding.lockedAmount > 0 && holding.amount === 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          unlockTokens(holding.contractId, holding.lockedAmount)
                            .then(() => refresh())
                            .catch(err => alert('Unlock failed: ' + (err?.message || err)));
                        }}
                        style={{ marginTop: '0.5rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', background: '#f59e0b', color: '#000' }}
                      >
                        <Lock size={14} /> Unlock Tokens
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setListingModal({ asset, holdingCid: holding.contractId });
                          setListingForm({ amount: (holding.amount || 1).toString(), pricePerToken: '' });
                        }}
                        style={{ marginTop: '0.5rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}
                      >
                        <Tag size={14} /> List for Sale
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {myProfile && (
            <div className="section">
              <h3>My Profile</h3>
              <div className="info-card">
                <div><strong>Status:</strong> {myProfile.status}</div>
                <div><strong>Jurisdiction:</strong> {myProfile.jurisdiction}</div>
                <div><strong>Balance:</strong> {formatPrice(myPaymentBalance)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* OPERATOR TAB */}
      {/* ============================================ */}
      {activeTab === 'operator' && roles?.isOperator && (
        <div className="tab-content">
          {/* Appoint Issuer */}
          <div className="section">
            <h2>Appoint Issuer</h2>
            <div className="form-row">
              <input
                placeholder="Party ID"
                value={newIssuer.party}
                onChange={e => setNewIssuer({ ...newIssuer, party: e.target.value })}
              />
              <input
                placeholder="Name"
                value={newIssuer.name}
                onChange={e => setNewIssuer({ ...newIssuer, name: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>Allowed Categories:</label>
              {ASSET_CATEGORIES.map(cat => (
                <label key={cat} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newIssuer.categories.includes(cat)}
                    onChange={e => {
                      if (e.target.checked) {
                        setNewIssuer({ ...newIssuer, categories: [...newIssuer.categories, cat] });
                      } else {
                        setNewIssuer({ ...newIssuer, categories: newIssuer.categories.filter(c => c !== cat) });
                      }
                    }}
                  />
                  {cat}
                </label>
              ))}
            </div>
            <button onClick={handleAppointIssuer} disabled={actionLoading}>
              {actionLoading ? 'Processing...' : 'Appoint Issuer'}
            </button>
          </div>

          {/* Current Issuers */}
          <div className="section">
            <h2>Licensed Issuers ({issuerLicenses.length})</h2>
            {issuerLicenses.length === 0 ? (
              <p>No issuers yet</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Party</th>
                    <th>Categories</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {issuerLicenses.map(lic => (
                    <tr key={lic.contractId}>
                      <td>{lic.issuerName}</td>
                      <td>{truncateParty(lic.issuer)}</td>
                      <td>{lic.allowedCategories.join(', ')}</td>
                      <td>
                        <button
                          className="btn-danger"
                          onClick={() => revokeIssuer(lic.contractId)}
                          disabled={actionLoading}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Listing Proposals */}
          <div className="section">
            <h2>Listing Proposals ({listingProposals.length})</h2>
            {listingProposals.length === 0 ? (
              <p>No pending listing proposals</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Seller</th>
                    <th>Amount</th>
                    <th>Price/Token</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listingProposals.map(prop => (
                    <tr key={prop.contractId}>
                      <td>{prop.assetName}</td>
                      <td>{truncateParty(prop.seller)}</td>
                      <td>{prop.amount}</td>
                      <td>{formatPrice(prop.pricePerToken)}</td>
                      <td>{formatPrice(prop.amount * prop.pricePerToken)}</td>
                      <td>
                        <button onClick={() => handleApproveListingProposal(prop.contractId)} disabled={actionLoading}>
                          Approve
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleRejectListingProposal(prop.contractId)}
                          disabled={actionLoading}
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Payment Deposit Requests */}
          <div className="section">
            <h2>Deposit Requests ({depositRequests.length})</h2>
            {depositRequests.length === 0 ? (
              <p>No pending deposit requests</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Token</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {depositRequests.map(req => (
                    <tr key={req.contractId}>
                      <td>{truncateParty(req.owner)}</td>
                      <td>{req.token}</td>
                      <td>{formatPrice(req.amount)}</td>
                      <td>
                        <button onClick={() => handleApproveDeposit(req.contractId)} disabled={actionLoading}>
                          Approve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Asset Proposals */}
          <div className="section">
            <h2>Pending Asset Proposals ({assetProposals.length})</h2>
            {assetProposals.length === 0 ? (
              <p>No pending proposals</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Supply</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assetProposals.map(prop => (
                    <tr key={prop.contractId}>
                      <td>{prop.name}</td>
                      <td>{prop.category}</td>
                      <td>{prop.assetType}</td>
                      <td>{prop.totalSupply}</td>
                      <td>
                        <button onClick={() => handleApproveAsset(prop.contractId)} disabled={actionLoading}>
                          Approve
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleRejectAsset(prop.contractId)}
                          disabled={actionLoading}
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pending Trades */}
          <div className="section">
            <h2>Pending Trades ({pendingTrades.length})</h2>
            {pendingTrades.length === 0 ? (
              <p>No pending trades</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Amount</th>
                    <th>Price</th>
                    <th>Seller</th>
                    <th>Buyer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTrades.map(trade => (
                    <tr key={trade.contractId}>
                      <td>{trade.assetId}</td>
                      <td>{trade.amount}</td>
                      <td>{formatPrice(trade.totalPrice)}</td>
                      <td>{truncateParty(trade.seller)}</td>
                      <td>{truncateParty(trade.buyer)}</td>
                      <td>
                        <button onClick={() => handleSettleTrade(trade.contractId)} disabled={actionLoading}>
                          Settle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ISSUER TAB */}
      {/* ============================================ */}
      {activeTab === 'issuer' && roles?.isIssuer && (
        <div className="tab-content">
          {/* Propose Asset */}
          <div className="section">
            <h2>Propose New Asset</h2>
            <p className="info">
              Allowed categories: {roles.issuerLicense?.allowedCategories.join(', ')}
            </p>
            <div className="form-grid">
              <input
                placeholder="Asset ID"
                value={newAsset.assetId}
                onChange={e => setNewAsset({ ...newAsset, assetId: e.target.value })}
              />
              <input
                placeholder="Name"
                value={newAsset.name}
                onChange={e => setNewAsset({ ...newAsset, name: e.target.value })}
              />
              <input
                placeholder="Symbol"
                value={newAsset.symbol}
                onChange={e => setNewAsset({ ...newAsset, symbol: e.target.value })}
              />
              <select
                value={newAsset.category}
                onChange={e => setNewAsset({ ...newAsset, category: e.target.value as AssetCategory })}
              >
                {(roles.issuerLicense?.allowedCategories || ASSET_CATEGORIES).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                value={newAsset.assetType}
                onChange={e => setNewAsset({ ...newAsset, assetType: e.target.value as AssetType })}
              >
                <option value="Fungible">Fungible</option>
                <option value="NonFungible">Non-Fungible</option>
              </select>
              <input
                type="number"
                placeholder="Total Supply"
                value={newAsset.totalSupply}
                onChange={e => setNewAsset({ ...newAsset, totalSupply: parseInt(e.target.value) || 0 })}
              />
              <input
                placeholder="Valuation (USD)"
                value={newAsset.valuationAmount}
                onChange={e => setNewAsset({ ...newAsset, valuationAmount: e.target.value })}
              />
              <textarea
                placeholder="Description"
                value={newAsset.description}
                onChange={e => setNewAsset({ ...newAsset, description: e.target.value })}
              />
            </div>
            <button onClick={handleProposeAsset} disabled={actionLoading}>
              {actionLoading ? 'Submitting...' : 'Submit Proposal'}
            </button>
          </div>

          {/* My Issued Assets */}
          <div className="section">
            <h2>My Issued Assets ({myIssuedAssets.length})</h2>
            {myIssuedAssets.length === 0 ? (
              <p>No assets issued yet</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Supply</th>
                    <th>Circulating</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myIssuedAssets.map(asset => (
                    <tr key={asset.contractId}>
                      <td>{asset.name}</td>
                      <td>{asset.category}</td>
                      <td>{asset.totalSupply}</td>
                      <td>{asset.circulatingSupply}</td>
                      <td>
                        <button onClick={() => setMintForm({ ...mintForm, assetContractId: asset.contractId })}>
                          Mint
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Mint Tokens */}
          {mintForm.assetContractId && (
            <div className="section">
              <h2>Mint Tokens</h2>
              <div className="form-row">
                <input
                  placeholder="Recipient Party ID"
                  value={mintForm.recipient}
                  onChange={e => setMintForm({ ...mintForm, recipient: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={mintForm.amount || ''}
                  onChange={e => setMintForm({ ...mintForm, amount: parseInt(e.target.value) || 0 })}
                />
                <button onClick={handleMint} disabled={actionLoading}>
                  {actionLoading ? 'Minting...' : 'Mint'}
                </button>
                <button onClick={() => setMintForm({ assetContractId: '', recipient: '', amount: 0 })}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* VERIFIER TAB */}
      {/* ============================================ */}
      {activeTab === 'verifier' && roles?.isVerifier && (
        <div className="tab-content">
          {/* Verify Investor */}
          <div className="section">
            <h2>Verify Investor</h2>
            <div className="form-row">
              <input
                placeholder="Investor Party ID"
                value={verifyForm.investorParty}
                onChange={e => setVerifyForm({ ...verifyForm, investorParty: e.target.value })}
              />
              <select
                value={verifyForm.status}
                onChange={e => setVerifyForm({ ...verifyForm, status: e.target.value as typeof verifyForm.status })}
              >
                <option value="Unverified">Unverified</option>
                <option value="KYCVerified">KYC Verified</option>
                <option value="AccreditedVerified">Accredited Verified</option>
                <option value="InstitutionalVerified">Institutional Verified</option>
              </select>
              <input
                placeholder="Jurisdiction"
                value={verifyForm.jurisdiction}
                onChange={e => setVerifyForm({ ...verifyForm, jurisdiction: e.target.value })}
              />
              <button onClick={handleVerify} disabled={actionLoading}>
                {actionLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>

          {/* Investor Profiles */}
          <div className="section">
            <h2>Investor Profiles ({allInvestorProfiles.length})</h2>
            {allInvestorProfiles.length === 0 ? (
              <p>No verified investors</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Investor</th>
                    <th>Status</th>
                    <th>Jurisdiction</th>
                  </tr>
                </thead>
                <tbody>
                  {allInvestorProfiles.map(profile => (
                    <tr key={profile.contractId}>
                      <td>{truncateParty(profile.investor)}</td>
                      <td>{profile.status}</td>
                      <td>{profile.jurisdiction}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ASSET DETAIL MODAL */}
      {/* ============================================ */}
      {selectedAsset && (
        <div className="modal-overlay" onClick={() => setSelectedAsset(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedAsset(null)}><X size={16} /></button>
            <div className="modal-content">
              <p className="modal-category">
                {selectedAsset.category} • {selectedAsset.assetType}
                {selectedAsset.assetType === 'NonFungible' && <span className="nft-badge" style={{ marginLeft: '0.5rem' }}>NFT</span>}
              </p>
              <h2>{selectedAsset.name}</h2>
              <p className="modal-description">{selectedAsset.description || selectedAsset.underlying.description}</p>

              <div className="modal-stats">
                <div className="modal-stat">
                  <span className="stat-label">Valuation</span>
                  <span className="stat-value">{formatPrice(parseFloat(selectedAsset.underlying.valuationAmount || '0'))}</span>
                </div>
                <div className="modal-stat">
                  <span className="stat-label">Total Supply</span>
                  <span className="stat-value">{selectedAsset.totalSupply}</span>
                </div>
                <div className="modal-stat">
                  <span className="stat-label">Circulating</span>
                  <span className="stat-value">{selectedAsset.circulatingSupply}</span>
                </div>
              </div>

              {/* Pawn Details */}
              {(() => {
                const meta = selectedAsset.metadata;
                const principal = meta?.find(m => m.key === 'principalAmount')?.value;
                if (!principal || principal === '0') return null;
                const rate = meta?.find(m => m.key === 'interestRate')?.value;
                const schedule = meta?.find(m => m.key === 'paymentSchedule')?.value;
                const txDate = meta?.find(m => m.key === 'transactionDate')?.value;
                const validity = meta?.find(m => m.key === 'validityDate')?.value;
                return (
                  <div className="modal-stats" style={{ marginTop: '1rem' }}>
                    <div className="modal-stat"><span className="stat-label">Principal</span><span className="stat-value">{formatPrice(parseFloat(principal))}</span></div>
                    {rate && rate !== '0' && <div className="modal-stat"><span className="stat-label">Interest Rate</span><span className="stat-value">{rate}%</span></div>}
                    {schedule && <div className="modal-stat"><span className="stat-label">Payment Schedule</span><span className="stat-value">{schedule}</span></div>}
                    {txDate && <div className="modal-stat"><span className="stat-label">Transaction Date</span><span className="stat-value">{txDate}</span></div>}
                    {validity && <div className="modal-stat"><span className="stat-label">Validity Date</span><span className="stat-value">{validity}</span></div>}
                  </div>
                );
              })()}

              {/* Digital Content / Media Viewers */}
              {(() => {
                const contentUrls: Array<{ label: string; url: string }> = [];
                if (selectedAsset.imageUrl) contentUrls.push({ label: 'Preview', url: selectedAsset.imageUrl });
                if (selectedAsset.metadata) {
                  selectedAsset.metadata.forEach(m => {
                    if (['modelCID', 'photoCID', 'videoCID', 'previewCID'].includes(m.key) && m.value) {
                      contentUrls.push({ label: m.key, url: m.value.startsWith('ipfs://') ? m.value : `ipfs://${m.value}` });
                    }
                  });
                }
                if (selectedAsset.underlying.documents?.length) {
                  selectedAsset.underlying.documents.forEach((doc, i) => {
                    if (doc) contentUrls.push({ label: `Document ${i + 1}`, url: doc });
                  });
                }

                if (contentUrls.length === 0) return null;

                return (
                  <div className="digital-content-section">
                    <h4>Digital Content</h4>
                    {contentUrls.map((item, i) => {
                      const httpUrl = ipfsToHttp(item.url);
                      const mediaType = getMediaType(httpUrl, item.label);
                      return (
                        <div key={i} className="media-item">
                          <p className="media-label">{item.label}</p>
                          {mediaType === 'model' && (
                            // @ts-expect-error model-viewer is a web component
                            <model-viewer
                              src={httpUrl}
                              alt={item.label}
                              auto-rotate
                              camera-controls
                              shadow-intensity="1"
                              style={{ width: '100%', height: '300px', borderRadius: '8px', background: 'rgba(0,0,0,0.2)' }}
                            />
                          )}
                          {mediaType === 'image' && (
                            <img src={httpUrl} alt={item.label} style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }} />
                          )}
                          {mediaType === 'video' && (
                            <video src={httpUrl} controls style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }} />
                          )}
                          {mediaType === 'pdf' && (
                            <iframe src={httpUrl} title={item.label} style={{ width: '100%', height: '400px', border: 'none', borderRadius: '8px' }} />
                          )}
                          {mediaType === 'unknown' && (
                            <a href={httpUrl} target="_blank" rel="noopener noreferrer" className="content-link">{item.url}</a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Metadata Table */}
              {selectedAsset.metadata && selectedAsset.metadata.length > 0 && (
                <div className="metadata-section">
                  <h4>Metadata</h4>
                  <table className="metadata-table">
                    <tbody>
                      {selectedAsset.metadata.map((m, i) => (
                        <tr key={i}>
                          <td className="metadata-key">{m.key}</td>
                          <td className="metadata-value">
                            {m.value.startsWith('ipfs://') ? (
                              <a href={ipfsToHttp(m.value)} target="_blank" rel="noopener noreferrer">{m.value}</a>
                            ) : m.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="compliance-section">
                <h4>Compliance</h4>
                <div className="compliance-flags">
                  {selectedAsset.compliance.requireKYC && <span>KYC Required</span>}
                  {selectedAsset.compliance.requireAccreditedInvestor && <span>Accredited Only</span>}
                  {selectedAsset.compliance.maxHolders && <span>Max {selectedAsset.compliance.maxHolders} holders</span>}
                  {!selectedAsset.compliance.requireKYC && !selectedAsset.compliance.requireAccreditedInvestor && !selectedAsset.compliance.maxHolders && (
                    <span>Open to all</span>
                  )}
                </div>
              </div>

              <div className="listings-section">
                <h4>Available Listings ({assetListings.length})</h4>
                {assetListings.length === 0 ? (
                  <p className="no-listings">No listings available for this asset</p>
                ) : (
                  <div className="listings-list">
                    {assetListings.map(listing => (
                      <div key={listing.contractId} className="listing-item">
                        <span>{listing.amount} tokens @ {formatPrice(listing.pricePerToken)}</span>
                        <span>Total: {formatPrice(listing.totalPrice)}</span>
                        <button
                          onClick={() => { setSelectedAsset(null); setPurchaseModal(listing); }}
                          disabled={actionLoading || myPaymentBalance < listing.totalPrice}
                        >
                          {actionLoading ? '...' : 'Buy'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* LISTING CREATION MODAL */}
      {/* ============================================ */}
      {listingModal && (
        <div className="modal-overlay" onClick={() => setListingModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setListingModal(null)}><X size={16} /></button>
            <div className="modal-content">
              <h2>List for Sale</h2>
              <p>{listingModal.asset.name}</p>
              <div className="form-grid">
                <div className="form-group">
                  <label>Amount to List</label>
                  <input
                    type="number"
                    value={listingForm.amount}
                    onChange={e => setListingForm({ ...listingForm, amount: e.target.value })}
                    placeholder="Amount"
                    max={myHoldings.find(h => h.assetId === listingModal.asset.assetId)?.amount || 0}
                    min={0}
                    step="any"
                  />
                </div>
                <div className="form-group">
                  <label>Price per Token (USD)</label>
                  <input
                    type="number"
                    value={listingForm.pricePerToken}
                    onChange={e => setListingForm({ ...listingForm, pricePerToken: e.target.value })}
                    placeholder="Price per token"
                    min={0}
                    step="any"
                  />
                </div>
              </div>
              {listingForm.amount && listingForm.pricePerToken && (
                <div className="modal-summary">
                  <strong>Total Value:</strong> {formatPrice(parseFloat(listingForm.amount) * parseFloat(listingForm.pricePerToken))}
                </div>
              )}
              <button
                onClick={handleCreateListingProposal}
                disabled={actionLoading || !listingForm.amount || !listingForm.pricePerToken}
                style={{ width: '100%' }}
              >
                {actionLoading ? 'Submitting...' : 'Submit Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* PURCHASE MODAL */}
      {/* ============================================ */}
      {purchaseModal && (
        <div className="modal-overlay" onClick={() => setPurchaseModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setPurchaseModal(null)}><X size={16} /></button>
            <div className="modal-content">
              <h2>Purchase</h2>
              <p>{purchaseModal.assetName}</p>
              <div className="modal-stats">
                <div className="modal-stat">
                  <span className="stat-label">Seller</span>
                  <span className="stat-value">{truncateParty(purchaseModal.seller)}</span>
                </div>
                <div className="modal-stat">
                  <span className="stat-label">Price/Token</span>
                  <span className="stat-value">{formatPrice(purchaseModal.pricePerToken)}</span>
                </div>
                <div className="modal-stat">
                  <span className="stat-label">Available</span>
                  <span className="stat-value">{purchaseModal.amount}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Amount to Purchase</label>
                <input
                  type="number"
                  value={purchaseForm.amount || purchaseModal.amount}
                  onChange={e => setPurchaseForm({ amount: e.target.value })}
                  max={purchaseModal.amount}
                  min={0}
                  step="any"
                />
              </div>
              {(() => {
                const amt = parseFloat(purchaseForm.amount) || purchaseModal.amount;
                const total = amt * purchaseModal.pricePerToken;
                const canAfford = myPaymentBalance >= total;
                return (
                  <>
                    <div className="modal-summary">
                      <div><strong>Total Cost:</strong> {formatPrice(total)}</div>
                      <div><strong>Your Balance:</strong> {formatPrice(myPaymentBalance)}</div>
                      {!canAfford && <div style={{ color: 'var(--error)', marginTop: '0.5rem' }}>Insufficient balance. Please deposit funds first.</div>}
                    </div>
                    <button
                      onClick={handlePurchase}
                      disabled={actionLoading || !canAfford}
                      style={{ width: '100%' }}
                    >
                      {actionLoading ? 'Processing...' : 'Confirm Purchase'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* DEPOSIT FUNDS MODAL */}
      {/* ============================================ */}
      {depositModal && (
        <div className="modal-overlay" onClick={() => setDepositModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setDepositModal(false)}><X size={16} /></button>
            <div className="modal-content">
              <h2>Deposit Funds</h2>
              <p>Request USDC deposit (operator must approve)</p>
              <div className="form-group">
                <label>Amount (USDC)</label>
                <input
                  type="number"
                  value={depositForm.amount}
                  onChange={e => setDepositForm({ amount: e.target.value })}
                  placeholder="Enter amount"
                  min={0}
                  step="any"
                />
              </div>
              <div className="modal-summary">
                <strong>Current Balance:</strong> {formatPrice(myPaymentBalance)}
              </div>
              <button
                onClick={handleRequestDeposit}
                disabled={actionLoading || !depositForm.amount || parseFloat(depositForm.amount) <= 0}
                style={{ width: '100%' }}
              >
                {actionLoading ? 'Submitting...' : 'Request Deposit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
